<script setup lang="ts">
import { computed, useId } from 'vue'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/dropdown-menu'
import LockedOption from '@/components/LockedOption.vue'
import { SELECT_TRIGGER_STATE_CLASSES } from '@/browse/select_trigger_classes'
import type { FacetOption } from '@/browse/browse_state'

const props = withDefaults(defineProps<{
  options: FacetOption[]
  labelledBy: string
  emptyText: string
  clearable?: boolean
  // Browse Present Levels keeps its plain trigger until its own pass.
  hasTriggerStates?: boolean
  unavailableOptionTextClass?: string
}>(), { clearable: true, hasTriggerStates: false, unavailableOptionTextClass: 'data-[disabled]:text-gray-400' })

const emit = defineEmits<{ select: [value: string] }>()

// Choosing this value clears the selection.
const NO_SELECTION = ''

// DropdownMenuRadioItem turns pointer events off on disabled items, which would hide the not-allowed cursor.
const UNAVAILABLE_OPTION_CLASSES = 'data-[disabled]:pointer-events-auto data-[disabled]:cursor-not-allowed data-[disabled]:hover:bg-transparent data-[disabled]:opacity-100'

// reka-ui focuses the option under the pointer, so the hover colours are restated over focus.
const OPTION_HOVER_CLASSES = 'hover:bg-blue-400 hover:focus:bg-blue-400 [&:not([data-disabled])]:hover:text-white [&:not([data-disabled])]:hover:focus:text-white'

const selectedOption = computed(() => props.options.find((option) => option.isSelected))
const selectedValue = computed(() => selectedOption.value?.value ?? NO_SELECTION)

function selectValue(value: unknown) {
  emit('select', String(value))
}

// Named by the section heading and the shown value, so the value is announced with it (WCAG 2.5.3).
// The value span carries the id because reka replaces the trigger's own.
const valueId = useId()
</script>

<template>
  <!-- non-modal: a modal menu marks the rest of the page aria-hidden while open (axe aria-hidden-focus) -->
  <DropdownMenu :modal="false">
    <DropdownMenuTrigger
      :aria-labelledby="`${labelledBy} ${valueId}`"
      class="flex w-full items-center justify-between rounded-xs border bg-white px-3 py-1.5 text-left text-text-md text-gray-900 focus-visible:outline-offset-2"
      :class="hasTriggerStates ? SELECT_TRIGGER_STATE_CLASSES : 'border-gray-600'"
    >
      <span :id="valueId" class="truncate">{{ selectedOption?.label ?? emptyText }}</span>
      <span class="material-icons-round text-[1em]" aria-hidden="true">arrow_drop_down</span>
    </DropdownMenuTrigger>

    <DropdownMenuContent
      align="start"
      :collision-padding="{ top: 64, bottom: 16 }"
      class="max-h-[var(--reka-popper-available-height)] min-w-[var(--reka-dropdown-menu-trigger-width)] overflow-y-auto bg-white"
    >
      <!-- reka gives the menu and its items tabindex -1, so axe needs a focusable element inside the scroll area (scrollable-region-focusable) -->
      <DropdownMenuRadioGroup tabindex="0" :model-value="selectedValue" @update:model-value="selectValue">
        <template v-if="clearable">
          <DropdownMenuRadioItem :value="NO_SELECTION" class="cursor-pointer" :class="OPTION_HOVER_CLASSES">{{ emptyText }}</DropdownMenuRadioItem>
          <DropdownMenuSeparator class="bg-gray-400" />
        </template>
        <DropdownMenuRadioItem
          v-for="option in options"
          :key="option.value"
          :value="option.value"
          :disabled="!option.isEnabled"
          class="cursor-pointer data-[disabled]:italic"
          :class="[OPTION_HOVER_CLASSES, {
            'data-[disabled]:cursor-not-allowed data-[disabled]:not-italic data-[disabled]:opacity-100': option.isLocked,
            [`${UNAVAILABLE_OPTION_CLASSES} ${unavailableOptionTextClass}`]: !option.isLocked
          }]"
        >
          <LockedOption v-if="option.isLocked" :label="option.label" />
          <template v-else>{{ option.label }}</template>
        </DropdownMenuRadioItem>
      </DropdownMenuRadioGroup>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
